import { useEffect, useState } from "react";

import {
  ACTIVE_PUT_VISUAL_MIN_MS,
  nextActivePutVisualState,
  type ActivePutVisualState,
} from "./activeByteTransferGuard";

const IDLE: ActivePutVisualState = {
  kind: null,
  startedAtMs: null,
  episodeFileCount: 0,
};

/**
 * Page-level visual notice for the active-PUT window.
 * Persists at least ACTIVE_PUT_VISUAL_MIN_MS; after PUT, switches to SAFE copy
 * until the minimum elapses. Does not control unload guards.
 * episodeFileCountHint drives singular/plural page copy (batch-aware).
 */
export function useActivePutVisualNotice(
  hasActivePut: boolean,
  episodeFileCountHint: number = 1,
): ActivePutVisualState {
  const [state, setState] = useState<ActivePutVisualState>(IDLE);

  useEffect(() => {
    setState((prev) =>
      nextActivePutVisualState(
        prev,
        hasActivePut,
        Date.now(),
        ACTIVE_PUT_VISUAL_MIN_MS,
        episodeFileCountHint,
      ),
    );
  }, [hasActivePut, episodeFileCountHint]);

  useEffect(() => {
    if (state.kind !== "safe" || state.startedAtMs == null) return undefined;
    const remaining = ACTIVE_PUT_VISUAL_MIN_MS - (Date.now() - state.startedAtMs);
    if (remaining <= 0) {
      setState((prev) =>
        nextActivePutVisualState(prev, false, Date.now(), ACTIVE_PUT_VISUAL_MIN_MS),
      );
      return undefined;
    }
    const timer = window.setTimeout(() => {
      setState((prev) =>
        nextActivePutVisualState(prev, false, Date.now(), ACTIVE_PUT_VISUAL_MIN_MS),
      );
    }, remaining);
    return () => window.clearTimeout(timer);
  }, [state.kind, state.startedAtMs]);

  return state;
}
